import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaHome, FaUser, FaCog, FaSignOutAlt, FaTicketAlt, FaTimes, FaTag, FaTasks } from "react-icons/fa";
import ATRAQ_LOGO_WITHOUT_LABEL from '../../../assets/ATRAQ_LOGO_WITHOUT_LABEL.png';

import usePocketBaseAuth from "../../../hooks/Auth/usePocketBaseAuth";
import { setSidebarCollapsed } from "../../../features/CompanySlice";
import { useDispatch } from "react-redux";
import { getOpenTicketsCount } from "../../../api/pocketbase";

const Sidebar = ({ isMobile = false, isMobileOpen = false, onClose }) => {
  const dispatch = useDispatch();
  const [loggedINUser, setLoggedINUser] = useState(null);
  const { logout } = usePocketBaseAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [ticketCount, setTicketCount] = useState(0);

  useEffect(() => {
    try {
      const PB_AUTH = JSON.parse(localStorage.getItem("pocketbase_auth") || "null");
      if (PB_AUTH) {
        setLoggedINUser(PB_AUTH.record);
      }
    } catch (error) {
      console.error("Error reading auth session:", error);
    }
    
    // Fetch open tickets count
    const fetchCount = async () => {
      const count = await getOpenTicketsCount();
      setTicketCount(count);
    };
    fetchCount();
  }, []);

  const renderAvatarOrInitials = () => {
    if (!loggedINUser) return null;

    if (loggedINUser.Avatar) {
      const constructImage = `https://dbap.a-traq.com/api/files/Contacts/${loggedINUser.id}/${loggedINUser.Avatar}`;
      return <img src={constructImage} alt="Avatar" className="w-8 h-8 rounded-full" />;
    } else {
      const initials = `${loggedINUser?.Name?.charAt(0) ?? ''}${loggedINUser?.Surname?.charAt(0) ?? ''}`;
      return (
        <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center text-white">
          {initials}
        </div>
      );
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    dispatch(setSidebarCollapsed(!isCollapsed));
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = () => {
    if (isMobile && onClose) {
      onClose();
    }
    logout();
  };

  const widthClass = isMobile ? "w-72 max-w-[85vw]" : isCollapsed ? "w-20" : "w-64";
  const containerClass = isMobile
    ? `fixed inset-y-0 left-0 z-50 flex h-full ${widthClass} flex-col bg-[#0e1726] text-white shadow-2xl transition-transform duration-300 md:hidden ${
        isMobileOpen ? "translate-x-0" : "-translate-x-full"
      }`
    : `hidden h-full shrink-0 self-stretch ${widthClass} flex-col bg-[#0e1726] text-white transition-all duration-300 md:flex`;

  return (
    <div className={containerClass}>
      {/* Sidebar Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-4">
        <div className="flex items-center space-x-2">
          <img src={ATRAQ_LOGO_WITHOUT_LABEL} alt="Logo" className="h-10 w-16 flex-shrink-0" />
          {(!isCollapsed || isMobile) && <h1 className="text-xl font-bold">ATraq</h1>}
        </div>
        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white transition hover:bg-white/10"
            aria-label="Close navigation"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {/* Menu Options */}
      <ul className="mt-4 flex-grow space-y-2 overflow-y-auto px-2">
        <li>
          <Link to="/dashboard" onClick={handleNavClick} className="flex items-center rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <FaHome className="mr-3" />
            {!isCollapsed && "Home"}
          </Link>
        </li>
        <li>
          <Link to="/companies" onClick={handleNavClick} className="flex items-center rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <FaUser className="mr-3" />
            {!isCollapsed && "Companies"}
          </Link>
        </li>
        <li>
          <Link to="/dynamics" onClick={handleNavClick} className="flex items-center rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <FaCog className="mr-3" />
            {!isCollapsed && "Dynamics"}
          </Link>
        </li>
        <li>
          <Link to="/tickets" onClick={handleNavClick} className="flex items-center justify-between rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <div className="flex items-center">
              <FaTicketAlt className="mr-3" />
              {!isCollapsed && "Tickets"}
            </div>
            {!isCollapsed && ticketCount > 0 && (
              <span className="bg-[#90c740] text-white text-xs font-bold px-2 py-1 rounded-full">
                {ticketCount}
              </span>
            )}
          </Link>
        </li>
        <li>
          <Link to="/tasks" onClick={handleNavClick} className="flex items-center rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <FaTasks className="mr-3" />
            {!isCollapsed && "Tasks"}
          </Link>
        </li>
        <li>
          <Link to="/coupons" onClick={handleNavClick} className="flex items-center rounded-xl px-4 py-3 hover:bg-gray-600 transition-colors">
            <FaTag className="mr-3" />
            {!isCollapsed && "Coupons"}
          </Link>
        </li>
        <li>
          <button onClick={handleLogout} className="flex w-full items-center rounded-xl px-4 py-3 text-left hover:bg-gray-600 transition-colors">
            <FaSignOutAlt className="mr-3" />
            {!isCollapsed && "Logout"}
          </button>
        </li>
      </ul>

      {/* User Profile Section */}
      {loggedINUser && (
        <div className="mb-4 flex items-center justify-center px-4">
          {renderAvatarOrInitials()}
          <div className="ml-4 min-w-0">
            {!isCollapsed && (
              <h2 className="truncate text-lg font-bold">
                {`${loggedINUser?.Name} ${loggedINUser?.Surname}`}
              </h2>
            )}
          </div>
        </div>
      )}

      {/* Toggle Sidebar Button */}
      {!isMobile && (
        <button onClick={toggleSidebar} className="flex w-full items-center justify-center bg-gray-800 px-4 py-4 hover:bg-gray-700 transition-colors">
          <span className="text-lg">{isCollapsed ? ">" : "<"}</span>
        </button>
      )}
    </div>
  );
};

export default Sidebar;
